import React, { FC, useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  AccordionSummary,
  AccordionDetails,
  Box,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import styled from 'styled-components';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { Toolbar } from "../components/FooterToolbar";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import IndicatorChart from "../components/IndicatorChart";
import CommentMessages from "../components/CommentMessages";
import { getTrainingIndicator, useAuth0Token } from "../common/http-requests";
import {
  trainingIndicatorRequestType,
  trainingIndicatorResponseType,
} from "../common/type";
import {
  useUserId,
  useUserNickname,
  useLanguage,
} from "../hooks/useStoreGetters";
import {
  descriptionText,
  commentTrainingIndicator,
} from "../common/Comment";
import { themeColors } from "../common/colors";

const BAR_CHART_SIZE: number[] = [
  1000, 320
];

// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '5px',
    padding: '20px',
    overflow: 'hidden'
  },
  accordionDetailsStyle: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center", // 水平方向の中央揃え
    justifyContent: "center", // 垂直方向の中央揃え
    minHeight: "200px", // 必要に応じて高さを設定
    width: `${BAR_CHART_SIZE[0]}px`, // 修正: 幅を親要素いっぱいに設定
    maxWidth: '100%',
    textAlign: "center", // 修正: テキストを中央揃え
    margin: "0 auto", // 修正: 要素を水平中央に配置
  },
});

const AccordionLabel = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${BAR_CHART_SIZE[0]}px`,
  margin: '8px auto 0',
  borderTop: `solid 1px ${themeColors[0]}`,
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
}));

const TrainingIndicatorPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();
  const userNickname = useUserNickname();
  const language = useLanguage();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const [tiMextData, setTIMextData] = useState<trainingIndicatorResponseType>({ training_indicators: [] });
  const [tiTokushimaData, setTITokushimaData] = useState<trainingIndicatorResponseType>({ training_indicators: [] });
  const [expandedMext, setExpandedMext] = useState(true);
  const [expandedTokushima, setExpandedTokushima] = useState(true);
  const toggleMext = () => setExpandedMext(!expandedMext);
  const toggleTokushima = () => setExpandedTokushima(!expandedTokushima);
  const [commentMessage, setCommentMessage] = useState<string>('');

  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  // 以下は、記録一覧画面のコードを流用した。
  // Phase1と異なり、画面遷移が必要ではないため、location（uselocation）は使用しない。
  // React18(2022年)より、strictMode（javascriptのコードを通常より厳しくエラーチェックできるモード）において
  // オフスクリーンから再度表示されることで、useEffectが２回発火（キック）される仕様変更となっため、1回に制限するよう変更した。
  // また、以下のコードの})();の();は、定義した関数を即時実行している。
  // 最後にreturn文にて定義された関数は、クリーンアップ関数であり、リスナー、オブザーバー等のオブジェクトを設定した場合、
  // レンダリング時に毎回インスタンスを作成されるため、メモリーリーク及びメモリーの圧迫を抑止するための関数。
  // useEffectの依存配列（第2引数）は、通常、空リストもしくは、具体的な変数のリストを登録するのが一般的であり、
  // 定義しない場合は、FCのstate, propsのうちいずれかが更新される度に実行されてしまうため、通常、定義しないことはない。
  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getTrainingIndicatorData(userId);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getTrainingIndicatorData = async (
    login_user_id: number,
  ) => {
    setIsSending(true);
    try {
      // 全国指標のデータを取得
      const requestMextParams: trainingIndicatorRequestType = {
        login_user_id,
        target_type: "mext",
      };
      const mextResponseData: trainingIndicatorResponseType = await getTrainingIndicator(
        await getToken(),
        requestMextParams
      );
      // console.log("★mextResponseData = ",mextResponseData);
      setTIMextData(mextResponseData);

      // 徳島県指標のデータを取得
      const requestTokushimaParams: trainingIndicatorRequestType = {
        login_user_id,
        target_type: "tokushima",
      };
      const tokushimaResponseData: trainingIndicatorResponseType = await getTrainingIndicator(
        await getToken(),
        requestTokushimaParams
      );
      // console.log("★tokushimaResponseData = ",tokushimaResponseData);
      setTITokushimaData(tokushimaResponseData);

      const comment = commentTrainingIndicator(login_user_id, userNickname, t, mextResponseData, tokushimaResponseData);
      setCommentMessage(comment);
    } catch {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const SummaryMessage = styled(Box)(() => ({
    maxWidth: `${BAR_CHART_SIZE[0]}px`,
    margin: isDesktop ? '3.5rem auto 1rem' : '7.5rem auto 1rem',
    textAlign: 'center',
  }));

  return (
    <Container maxWidth="xl">
      {isSending ?
      <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
      (
        <>
          <VisualizedMenus />
          {feedbackInfo.open &&
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
          }
          <SummaryMessage>{descriptionText(userNickname, language, t, "indicator")}<br/>{t("common.chartNotation")}</SummaryMessage>
          <CommentMessages message={commentMessage} />
          <Paper className={classes.chartContainer}>
            <AccordionLabel>
              <AccordionSummary
                onClick={toggleMext}
                expandIcon={expandedMext ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                <Typography>{t("indicator.title_mext")}</Typography>
              </AccordionSummary>
            </AccordionLabel>
            {expandedMext && tiMextData?.training_indicators.length > 0 && (
              <AccordionDetails className={classes.accordionDetailsStyle}>
                <IndicatorChart tiDataType="mext" tiData={tiMextData} />
              </AccordionDetails>
            )}
            <AccordionLabel>
              <AccordionSummary
                onClick={toggleTokushima}
                expandIcon={expandedTokushima ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                <Typography>{t("indicator.title_tokushima")}</Typography>
              </AccordionSummary>
            </AccordionLabel>
            {expandedTokushima && tiTokushimaData?.training_indicators.length > 0 && (
              <AccordionDetails className={classes.accordionDetailsStyle}>
                <IndicatorChart tiDataType="tokushima" tiData={tiTokushimaData} />
              </AccordionDetails>
            )}
          </Paper>
        </>
      )}
      <Toolbar />
    </Container>
  );
};

export default TrainingIndicatorPage;
